<script lang="ts" setup>
import fontColorContrast from 'font-color-contrast'

import AspectRatioCanvas from '~/components/ui/AspectRatioCanvas.vue'

import { useGetMediaUrl } from '~/composables/useGetMediaUrl'

import { provideAspectRatioUtils } from '~/helpers/AspectRatioCanvas'

import {
  getCenteredTextMeasurements,
  roundedClip,
  truncateText,
  writeTextCentered,
} from '~/utils/canvas'
import { loadCorsImage } from '~/utils/removeCorsFromCoverArt'

type Props = {
  trackPictureUrl: string
  trackTitle: string
  bandName: string
  /** Hexadecimal value */
  selectedColor: string
  /** Hexadecimal value */
  accentColor: string
  descriptor: string
  rank: number
}

const EXPECTED_IMAGE_SIDE_SIZE = 266

const props = defineProps<Props>()

const { getMediaUrl } = useGetMediaUrl()

enum Color {
  'lin' = '#F7F6F0',
  'charcoal' = '#2B2B2B',
  'descrete' = '#525252',
  'black' = '#000000',
  'white' = '#ffffff',
}

/** Will be recomputed on render based of `props.selectedColor` and the {@link fontColorContrast|font color contrast} package */
const textColor = ref(getTextColor())
/** Image object for the loaded track image, will re-render on change */
const trackImage = shallowRef<InstanceType<typeof Image> | null>(null)
/** Image object for the loaded groove logo image, will **not** re-render on change */
const grooverLogo = shallowRef<InstanceType<typeof Image> | null>(null)
/** Generic component named Aspect Ratio Canvas that exposes a bunch of utils to component canvas UGCs */
const arc = useTemplateRef('aspectRatioCanvas')

const { normalizeX } = provideAspectRatioUtils()

const iverseTextColor = computed(() =>
  props.selectedColor === (Color.lin as const)
    ? (Color.charcoal as const)
    : textColor.value === (Color.lin as const)
      ? (Color.descrete as const)
      : (Color.lin as const),
)

function handleCanvasIsReady() {
  grooverLogo.value = new Image()
  grooverLogo.value.onload = () =>
    loadCorsImage(trackImage, props.trackPictureUrl, () => render())
  grooverLogo.value.crossOrigin = 'anonymous'
  grooverLogo.value.src = getMediaUrl(
    textColor.value === '#F7F6F0'
      ? '/GrooverChartsLogo-Lin.svg'
      : '/GrooverChartsLogo-Charcoal.svg',
  )
}

watch(
  () => [props.trackPictureUrl, props.selectedColor],
  () => handleCanvasIsReady(),
)

watch(
  () => [
    props.bandName,
    props.trackTitle,
    props.descriptor,
    props.selectedColor,
    props.accentColor,
    props.rank,
  ],
  () => render(),
)

function getTextColor() {
  return fontColorContrast(props.selectedColor) === Color.black
    ? (Color.charcoal as const)
    : (Color.lin as const)
}

/**
 * Main render function. Similarily to `components/track/SubmissionShareCanvas.vue`, we render from top to bottom back to front.
 */
function render() {
  const ctx = arc.value?.canvasContext
  const canvas = arc.value?.canvas

  textColor.value = getTextColor()
  if (!ctx || !canvas) return

  ctx.globalAlpha = 1
  ctx.textBaseline = 'middle'

  drawBackground(ctx, canvas)
  drawHeadLogo(ctx, canvas)
  drawTitle(ctx, canvas)
  drawCoverArt(ctx, canvas)
  drawRank(ctx, canvas)
  drawBottomTextBoxes(ctx, canvas)
}

function drawHeadLogo(ctx: CanvasRenderingContext2D, _: HTMLCanvasElement) {
  if (!grooverLogo.value) return

  ctx.drawImage(grooverLogo.value, 42, 64, 285, 30)
}

function drawTitle(
  ctx: CanvasRenderingContext2D,
  canvasElement: HTMLCanvasElement,
) {
  ctx.fillStyle = textColor.value
  return writeTextCentered(
    ctx,
    canvasElement,
    props.descriptor,
    20,
    112,
    '450 18px Circular',
    textColor.value,
    normalizeX,
  )
}

function drawBackground(
  ctx: CanvasRenderingContext2D,
  canvasElement: HTMLCanvasElement,
) {
  ctx.fillStyle = props.accentColor
  ctx.fillRect(0, 0, canvasElement.width, canvasElement.height)
  ctx.fillStyle = props.selectedColor

  // Render svg
  const svg = new Path2D()
  // This matice scaling is very arbitrary but it' s giving 24px
  svg.addPath(
    new Path2D(
      'M1264.63 847.895C1373.05 637.106 1374.19 409.455 1282.75 264.658C1277.85 256.873 1270.61 250.635 1262.4 246.435L1021.85 123.506C1013.64 119.307 1004.49 117.173 995.129 117.67C899.504 123.552 799.463 168.089 711.091 242.79C706.571 246.631 699.792 242.498 701.294 236.714C725.131 137.523 729.253 49.8485 713.022 -14.4003C709.288 -29.0099 699.424 -41.2039 685.958 -48.0859L570.061 -107.315C556.435 -114.278 540.744 -115.144 526.582 -109.479C448.277 -78.3012 358.115 1.56904 275.629 113.639C271.354 119.477 262.451 114.526 265.029 107.821C312.12 -14.316 336.961 -122.487 335.567 -195.665C335.192 -215.111 324.322 -232.899 307.038 -241.732L253.543 -269.071C236.206 -277.93 215.746 -276.554 199.661 -265.588C145.925 -228.806 81.8909 -159.249 17.9922 -67.6954C13.7948 -61.6178 4.52104 -66.7584 7.23396 -73.5946C47.2713 -172.128 73.7264 -257.012 83.1181 -316.924C86.554 -338.833 74.8995 -360.433 55.0707 -370.566L51.0413 -372.625C30.3643 -383.192 5.96661 -380.017 -9.95717 -364.29C-83.9672 -291.608 -188.708 -135.492 -286.732 55.0764C-379.387 234.817 -443.431 401.37 -463.912 505.619C-468.342 528.29 -456.728 551.139 -436.21 561.625L-422.902 568.425C-402.225 578.992 -376.752 574.897 -360.869 558.078C-320.069 514.685 -271.377 448.92 -220.257 368.15C-216.431 361.883 -206.753 366.628 -209.172 373.414C-242.606 467.701 -261.615 551.778 -264.165 614.919C-264.869 635.684 -253.881 654.803 -235.537 664.178L-167.886 698.751C-149.542 708.126 -127.634 706.018 -111.371 693.405C-54.1639 648.704 12.7604 570.663 78.4535 470.935C82.4102 464.935 91.5579 469.409 89.3795 476.117C50.122 596.174 36.27 706.151 49.2099 786.299C51.8056 802.6 62.3715 816.423 77.1635 823.983L215.435 894.646C230.227 902.205 247.601 902.661 262.476 895.221C318.193 867.474 379.304 816.746 438.07 748.708C441.905 744.116 449.282 747.218 448.791 753.117C440.567 855.407 456.991 951.041 496.515 1027.93C501.424 1037.39 509.472 1044.91 519.069 1049.82L801.655 1194.23C811.251 1199.14 822.077 1201.26 832.731 1199.55C994.423 1175.09 1163.15 1045.32 1264.68 847.922',
    ),
    new DOMMatrix().translateSelf().scaleSelf(),
  )
  ctx.fill(svg)
}

function drawCoverArt(ctx: CanvasRenderingContext2D, _: HTMLCanvasElement) {
  if (!trackImage.value) return

  const x = 55
  const y = 142

  ctx.save()
  roundedClip(ctx, {
    x,
    y,
    width: EXPECTED_IMAGE_SIDE_SIZE,
    height: EXPECTED_IMAGE_SIDE_SIZE,
    radius: 12,
  })
  // Clip the context
  ctx.clip()
  const imageIsYt = /ytimg/gi.test(props.trackPictureUrl)
  const imageSize = Math.min(
    trackImage.value.width,
    trackImage.value.height - (imageIsYt ? 90 : 0),
  )

  const left = (trackImage.value.width - imageSize) / 2
  const top = (trackImage.value.height - imageSize) / 2

  ctx.drawImage(
    trackImage.value,
    left,
    top,
    imageSize,
    imageSize,
    x,
    y,
    EXPECTED_IMAGE_SIDE_SIZE,
    EXPECTED_IMAGE_SIDE_SIZE,
  )

  ctx.restore()
}

function drawRank(
  ctx: CanvasRenderingContext2D,
  canvasElement: HTMLCanvasElement,
) {
  if (props.rank >= 100) return

  const RANK_Y = 349
  const {
    measurements: { actualBoundingBoxAscent: rankTextHeight },
  } = getCenteredTextMeasurements(
    ctx,
    canvasElement,
    props.rank.toString(),
    0,
    RANK_Y,
    '900 230px "neue-haas-display"',
    textColor.value,
    normalizeX,
  )
  ctx.textAlign = 'left'
  ctx.fillText(props.rank.toString(), 68, RANK_Y + rankTextHeight)

  const {
    measurements: { actualBoundingBoxAscent: poundTextHeight },
  } = getCenteredTextMeasurements(
    ctx,
    canvasElement,
    '#',
    22,
    RANK_Y,
    '900 90px "neue-haas-display"',
    textColor.value,
    normalizeX,
  )
  ctx.textAlign = 'left'
  ctx.fillText('#', 15, RANK_Y + poundTextHeight + rankTextHeight + 12)
}

function drawBottomTextBoxes(
  ctx: CanvasRenderingContext2D,
  canvasElement: HTMLCanvasElement,
) {
  ctx.save()
  const localTextColor =
    props.selectedColor === Color.lin ? Color.lin : textColor.value

  console.log(localTextColor, props.selectedColor, Color)
  const BOX_PADDING = 8
  const BOX_RADIUS = 12
  const BOX_X = 52
  const TRACK_TITLE_MAX_WIDTH = 267

  ctx.font = '900 20px "neue-haas-display"'
  const trackTitleText = truncateText(
    props.trackTitle.replace(/\s+/g, ' '),
    TRACK_TITLE_MAX_WIDTH,
    ctx,
  )

  const measurements = ctx.measureText(trackTitleText)
  const trackTitleBoxHeight = 24 * 2
  const TRACK_TITLE_BOX_Y = 491
  roundedClip(ctx, {
    x: BOX_X,
    y: TRACK_TITLE_BOX_Y,
    width: measurements.width + BOX_PADDING * 2,
    height: trackTitleBoxHeight,
    radius: BOX_RADIUS,
  })
  ctx.fillStyle = iverseTextColor.value
  ctx.clip()
  ctx.fillRect(0, 0, canvasElement.width, canvasElement.height)
  ctx.textAlign = 'left'
  ctx.fillStyle = localTextColor
  ctx.fillText(
    trackTitleText,
    BOX_X + BOX_PADDING,
    TRACK_TITLE_BOX_Y + trackTitleBoxHeight / 2,
  )
  ctx.restore()

  const BAND_NAME_MAX_WIDTH = 248
  ctx.font = '450 16px Circular'
  ctx.textAlign = 'left'
  const bandNameText = truncateText(
    props.bandName.replace(/\s+/g, ' '),
    BAND_NAME_MAX_WIDTH,
    ctx,
  )
  const bandNameMeasurements = ctx.measureText(bandNameText)
  const bandNameBoxY = trackTitleBoxHeight + TRACK_TITLE_BOX_Y
  const bandNameBoxHeight = 20 * 2
  ctx.save()
  roundedClip(ctx, {
    x: BOX_X,
    y: bandNameBoxY,
    width: bandNameMeasurements.width + BOX_PADDING * 2,
    height: bandNameBoxHeight,
    radius: BOX_RADIUS,
  })
  ctx.fillStyle = iverseTextColor.value
  ctx.clip()
  ctx.fillRect(0, 0, canvasElement.width, canvasElement.height)
  ctx.fillStyle = localTextColor
  ctx.fillText(
    bandNameText,
    BOX_X + BOX_PADDING,
    bandNameBoxY + bandNameBoxHeight / 2,
  )
  ctx.restore()
}

defineExpose({
  getImage() {
    const canvas = arc.value?.canvas

    if (!canvas) return
    return canvas.toDataURL()
  },
})
</script>

<template>
  <AspectRatioCanvas @ready="handleCanvasIsReady" ref="aspectRatioCanvas" />
</template>
